import { getStoredTradesWidths } from '@/features/trades/components/tables/trades-width-storage';

const MAX_SAMPLE_ROWS = 200;

// A column must be at least as wide as its own header label. The label renders with the
// header row's `text-xs font-semibold uppercase tracking-wider` (TradesTable.tsx): Inter 600
// at 12 px, upper-cased, +0.6 px letter-spacing. Shaping all 58 default labels with exactly
// those styles (uharfbuzz, Inter latin — see docs/reviews/dzh/LR-668-review-r2.md) gives
// ~6.6 px per glyph on average; HEADER_CHAR_PX = 7.5 is a deliberate upper bound over that
// measurement, not a property of the font size. The cell's own padding (`px-1.5 box-content`)
// sits OUTSIDE `width`, so the only chrome inside the width is the sort-icon slot (12) plus
// its margins — about 22 px; HEADER_CHROME_PX = 40 covers it with ≥ 18 px to spare. Measured
// slack left after the chrome: min ≈ 20 px ("ID"), max ≈ 41 px ("Cash Pool Interest Accrual").
// Filter / pin icon widths are added later by getColWidth, so they are not counted here.
// Do not "recalibrate" these numbers from the font size alone — re-shape the labels first.
// Before this floor a hard `size` won outright, and any label longer than the size was
// ellipsised ("Last Marked Gross P…", "Security…", "Settlement…") while its neighbours with
// the same text length but a larger `size` showed in full (LR-668).
const HEADER_CHAR_PX = 7.5;
const HEADER_CHROME_PX = 40;

interface TradesColumn {
  accessorKey?: string;
  id?: string;
  size?: number;
  /** ColumnDef.header — only string labels contribute to the width floor. */
  header?: unknown;
}

/** Minimum column width (px) that fits the given header label; 0 for non-string headers. */
export const headerMinWidth = (header: unknown): number =>
  typeof header === 'string' && header.length > 0
    ? Math.ceil(header.length * HEADER_CHAR_PX) + HEADER_CHROME_PX
    : 0;

export const calculateTradesColumnWidths = <T>(
  columns: TradesColumn[],
  data: readonly T[]
): Record<string, number> => {
  const storedWidths = getStoredTradesWidths();
  const widths: Record<string, number> = {};
  const sampleData = data.length > MAX_SAMPLE_ROWS ? data.slice(0, MAX_SAMPLE_ROWS) : data;

  columns.forEach((column) => {
    const columnId = column.accessorKey || column.id;
    if (!columnId) return;
    const headerMin = headerMinWidth(column.header);
    if (column.size) {
      widths[columnId] = Math.max(column.size, headerMin);
      return;
    }
    if (columnId === 'rowNumber') {
      widths[columnId] = 40;
      return;
    }
    let maxContentWidth = 80;
    sampleData.forEach((row) => {
      const value = (row as Record<string, unknown>)[columnId];
      const stringValue = String(value || '');
      let charWidth = 7;
      if (['id', 'isin', 'created_by', 'created_at', 'updated_at'].includes(columnId)) {
        charWidth = 7;
      } else if (['counterparty', 'symbol_id', 'description'].includes(columnId)) {
        charWidth = 8;
      }
      const contentWidth = stringValue.length * charWidth + 24;
      maxContentWidth = Math.max(maxContentWidth, contentWidth);
    });
    const calculated = Math.min(maxContentWidth, 300);
    // Merge with stored widths: use the larger of calculated vs stored to prevent shrinking
    widths[columnId] = Math.max(calculated, storedWidths[columnId] ?? 0, headerMin);
  });
  return widths;
};
